// BRIEF 86 PHASE 2, STEP 5 (run by brief 90 §5) — the stage timeline, once.
// 109 §7 — RECOMPOSED (K1/K2/K5/K6): the row says what a stage is
// (Type · By · Jurisdiction, with RG and the date as identity), and the
// detailed-modal path for stage documents is retired in favour of the same
// two drawer icons tasks and events carry:
//
//   Documents — a TaskDocsPane-MECHANICS drawer (not the component: stage
//   documents are rows of the CASE's docs[], not TaskDoc rows): simple
//   [open icon · name] rows filtered by `stageId === st.id`, plus the 108 §1
//   label+link+OneDrive add dialog, writing through the HOST's own doc write
//   path with `stageId` stamped (`onCreateStageDoc`). The host document
//   pane's richer row (status/category/move-to-stage) REMAINS that pane's
//   business — none of it is duplicated here.
//
//   Notes — the §1d discreet pattern (NotesDrawer) bound to `st.notes`,
//   written through the host's stages JSON path (`onSaveStageNotes`).
//   Lock gating follows §1: docs STRUCTURE is edit-gated (`stgLocked`),
//   notes are always editable.
//
//   The link pill (7f) — where events carry `stageId === st.id`, the row
//   grows a pill after the icons naming the event (count if several);
//   click = jump + amber flash via the existing scrollToEv. Stage rows carry
//   `id="stg-{id}"` so scrollToStage can jump back.
//
// The 86-era host handles that survive: `onEditStage`, `onDeleteStage` (the
// host's own store writes), and the stages/docs/ju feeds. `docRow` and
// `onAddDoc` left with the affordances they served.
import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { AddButton, openDocHref } from "./SharedUI";
import DocumentEntryModal from "./modals/DocumentEntryModal"; // 151 §2 — the one modal
import { DocTypeBadge } from "./DocTypeBadge"; // 151 §3 — the row's file-type badge
import { DocsToggle, NotesToggle } from "./RowDrawerToggle";
import NotesDrawer, { hasNoteText } from "./NotesDrawer"; // 118 §9
import LinkIcon from "./LinkIcon"; // 111 §5c
import TaxonomyOrphanMark from "./TaxonomyOrphanMark";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import type { TaxList, TFn } from "../lib/taxonomyTypes";
import { fd, I, scrollToEv, FT_INFO, detFT, dcatTag, dcatLabel } from "../utils";
import { RAD, FS } from "../lib/tokens";

// The read-sets this block uses. Partial by design, and typed rather than `any`
// because the ratchet allows a new file none (SESSION_RULES, "A ratchet is not
// re-recorded to make it pass").
export type Stage = {
  id: number;
  type?: string | null;
  by?: string | null;
  notes?: string | null;
  registerNumber?: string | number | null;
  startDate?: string | null;
  juId?: number | string | null;
  [k: string]: unknown;
};
type StageDoc = {
  id: number;
  stageId?: number | null;
  date?: string | null;
  name?: string | null;
  link?: string | null;
  /* 151 §3 — the row now reads the classification the modal writes */
  fileType?: string | null;
  type?: string | null;
  status?: string | null;
  note?: string | null;
  [k: string]: unknown;
};
type Jurisdiction = { id: number | string; name?: string; [k: string]: unknown };
type LinkedEvent = { id: number; title?: string | null; stageId?: number | string | null; [k: string]: unknown };

/* 186 (e) [RULED 186 (16)] — THE SEARCH FIELD'S PREDICATE, once, beside the row
   it reads: a stage matches when the query is found in what the row SHOWS —
   the type's label, By's label (`resolveTaxonomyLabel` hands the code back
   when no entry matches, as the row's `|| st.by` does), the jurisdiction's
   name, the register number, the date as rendered, or the note. Both hosts
   filter their own list with it
   (CaseView before its page, CCV over the whole list) and render the shared
   `SectionSearch`; the component itself renders no field and is otherwise
   untouched. Exported so the two mounts cannot drift into two predicates. */
export function stageMatchesQuery(
  st: Stage,
  needle: string,
  feeds: { caseStageEntries: TaxList; partyRoleEntries: TaxList; ju: Jurisdiction[] },
): boolean {
  const q = needle.trim().toLowerCase();
  if (!q) return true;
  const sjr = feeds.ju.find((j) => j.id === st.juId);
  const parts = [
    resolveTaxonomyLabel(st.type, feeds.caseStageEntries),
    st.by ? resolveTaxonomyLabel(st.by, feeds.partyRoleEntries) : "",
    sjr ? sjr.name : "",
    st.registerNumber == null ? "" : String(st.registerNumber),
    st.startDate ? fd(st.startDate) : "",
    st.notes ?? "",
  ];
  return parts.some((v) => String(v ?? "").toLowerCase().includes(q));
}

type Props = {
  // The stages to render — a page of them in CaseView, all of them in CCV.
  stages: Stage[];
  /* 111 §5c — the matter row (itemLinks feed for the Link icon). */
  matterRow?: { itemLinks?: unknown } | null;
  // Every document on the matter; the block filters per stage itself.
  docs: StageDoc[];
  ju: Jurisdiction[];
  _caseStageEntries: TaxList;
  // 109 §7a — the party_role entries, for `st.by`; the App-altitude feed both
  // views already hold (threaded, never refetched).
  partyRoleEntries: TaxList;
  // 109 §7f — the matter's agenda events, for the link pill.
  events: LinkedEvent[];
  stagesOpen: boolean;
  stgExp: Set<unknown>;
  setStgExp: (fn: (s: Set<unknown>) => Set<unknown>) => void;
  stgLocked: boolean;
  /* 151 §2 step 3 — the modal's Link to Stage select needs the matter's FULL
     stage list: CaseView hands `stages` a PAGE (10 per side), and a select
     built from a page would make every other stage unreachable. Both hosts
     pass their full sorted list here. */
  allStages: Stage[];
  /* 151 §2 step 3 — (iv)'s Type and Status of document selects: the hosts'
     own taxonomy feeds (document_category tuple list · document_status vlc
     list), threaded like partyRoleEntries above, never refetched. */
  docTypesList: ReadonlyArray<readonly [string, string]>;
  docStatusesList: ReadonlyArray<{ v: string; l: string; c?: string }>;
  // 109 §7b — the host's own doc write path. 151 §2 — the payload carries the
  // modal's inferred trio beside name/link, and step 3 adds (iv)'s completed
  // fields (type · status · note); the stageId argument is the DRAFT's — the
  // modal's Link to Stage select, initialised to the drawer that opened it.
  onCreateStageDoc: (
    stageId: number,
    doc: {
      name: string;
      link: string;
      fileType?: string;
      date?: string;
      source?: string;
      type?: string;
      status?: string;
      note?: string;
    },
  ) => void;
  /* 151 §3 (x) — the pencil's write: the host corrects its own docs[] row in
     place, every field the modal edits, stageId included (the Link to Stage
     select IS the move-to-stage now — the §4-doomed pane's select retires
     into it). */
  onUpdateStageDoc: (
    docId: number,
    doc: {
      name: string;
      link: string;
      fileType?: string;
      date?: string;
      source?: string;
      type?: string;
      status?: string;
      note?: string;
      stageId?: number | null;
    },
  ) => void;
  /* 151 §3 (x) — the bin: each host keeps its own delete UX (CV's
     confirm-and-undo, CCV's delete-and-toast — the panes' own shapes). */
  onDeleteStageDoc: (doc: StageDoc) => void;
  // 109 §7b — the host's stages-JSON write path for the note.
  onSaveStageNotes: (stageId: number, notes: string | null) => void;
  // The matter binding for the OneDrive picker; absent = no OneDrive button.
  oneDrive?: { caseId?: number | string; ccId?: number | string } | null;
  onEditStage: (st: Stage) => void;
  onDeleteStage: (st: Stage) => void;
  t: TFn;
};

export default function CaseStageTimeline({
  stages,
  docs,
  /* 111 §5c — the matter row holding itemLinks (both hosts pass their own
     subject `cs`). Typed loose beside the other feeds. */
  matterRow,
  ju,
  _caseStageEntries,
  partyRoleEntries,
  events,
  stagesOpen,
  stgExp,
  setStgExp,
  stgLocked,
  allStages,
  docTypesList,
  docStatusesList,
  onCreateStageDoc,
  onUpdateStageDoc,
  onDeleteStageDoc,
  onSaveStageNotes,
  oneDrive,
  onEditStage,
  onDeleteStage,
  t,
}: Props) {
  // §7b — the notes drawer's own disclosure, like every row drawer.
  const [notesOpen, setNotesOpen] = useState<Set<number>>(new Set());
  // §7b — the add dialog: which stage it adds to (null = closed), + the draft.
  /* 151 §2 — the dialog's draft moved into DocumentEntryModal (internal,
     reset by unmount); this component keeps only WHICH stage is adding. */
  const [addFor, setAddFor] = useState<number | null>(null);
  /* 151 §3 (x) — the pencil's subject (null = not editing); the one modal
     serves add and edit from a single mount below. */
  const [editDoc, setEditDoc] = useState<StageDoc | null>(null);
  return (
    <div className="timeline">
      {stages.map((st: Stage) => {
        /* 151 §3 — dated rows ascending, UNDATED LAST (the te table's rule):
           a casefile item that answered no last-modified stores date "" and
           `new Date("")` is Invalid — a NaN comparator leaves the order
           browser-defined, which is no order at all. */
        const stDocs = docs
          .filter((d: StageDoc) => d.stageId === st.id)
          .sort((a: StageDoc, b: StageDoc) => {
            const av = a.date ? +new Date(a.date as string) : NaN;
            const bv = b.date ? +new Date(b.date as string) : NaN;
            const aBad = Number.isNaN(av);
            const bBad = Number.isNaN(bv);
            if (aBad && bBad) return 0;
            if (aBad) return 1;
            if (bBad) return -1;
            return av - bv;
          });
        const isCol = !stagesOpen;
        const sjr = ju.find(
          (j: Jurisdiction) => j.id === st.juId,
        );
        const byLabel = st.by
          ? resolveTaxonomyLabel(st.by, partyRoleEntries) || st.by
          : null;
        /* 110 §6a — the drawers obey ONLY their own icons. The old
           `stagesOpen ||` term could force every stage's docs open (and the
           empty-docs line with them); on this tree it was DEAD — measured:
           `setStagesOpen` has no caller in either host, so the flag is
           permanently false — and it is removed so nothing can wire the
           coupling back. `stagesOpen` keeps its other job (row density,
           `isCol` above) untouched. */
        const docsShown = stgExp.has(st.id);
        const linked = (events || []).filter(
          (e) => e && e.stageId != null && Number(e.stageId) === Number(st.id),
        );
        return (
          <div
            key={st.id}
            id={"stg-" + st.id}
            data-lnk={"stage-" + st.id}
            className="timeline-item"
            style={{
              marginBottom: isCol ? 3 : 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: FS.s11_5,
                  fontWeight: 600,
                  color: "var(--tp)",
                }}
              >
                {resolveTaxonomyLabel(
                  st.type,
                  _caseStageEntries,
                )}
                <TaxonomyOrphanMark value={st.type} entries={_caseStageEntries} t={t} />
              </span>
              {/* §7a — By, resolved through the party_role entries; omitted
                  cleanly when unset. */}
              {byLabel && (
                <span
                  style={{
                    fontSize: FS.s10,
                    color: "var(--ts)",
                  }}
                >
                  {byLabel}
                </span>
              )}
              {/* §7a — the jurisdiction moves up beside By. */}
              {sjr && (
                <span
                  style={{
                    fontSize: FS.s10,
                    color: "var(--tm)",
                  }}
                >
                  {sjr.name}
                </span>
              )}
              {st.registerNumber && (
                <span
                  className="mono"
                  style={{
                    fontSize: FS.s10,
                    color: "var(--ts)",
                  }}
                >
                  {t("cc.stage.rg_prefix", { fallback: "RG" })}{" "}
                  {st.registerNumber}
                </span>
              )}
              {/* §7a — the date is KEPT (the brief's recomposition ruled on
                  the notes excerpt and the register; a dated timeline row
                  stays dated — enumerated in the PR). */}
              {st.startDate && (
                <span
                  className="mono"
                  style={{
                    fontSize: FS.s10,
                    color: "var(--tm)",
                  }}
                >
                  {fd(st.startDate)}
                </span>
              )}
              {/* §7b — the two icons tasks and events carry, in their shared
                  chrome. Docs: structure, so the toggle hides only when locked
                  AND empty (docsToggleVisible's rule); Notes: always (§1d). */}
              {(stDocs.length > 0 || !stgLocked) && (
                <DocsToggle
                  open={docsShown}
                  hasDocs={stDocs.length > 0}
                  onToggle={() =>
                    setStgExp((sset) => {
                      const n = new Set(sset);
                      n.has(st.id) ? n.delete(st.id) : n.add(st.id);
                      return n;
                    })
                  }
                />
              )}
              <NotesToggle
                open={notesOpen.has(st.id)}
                hasNotes={hasNoteText(st.notes)} /* 118 §9 */
                onToggle={() =>
                  setNotesOpen((prev) => {
                    const n = new Set(prev);
                    n.has(st.id) ? n.delete(st.id) : n.add(st.id);
                    return n;
                  })
                }
              />
              {/* 111 §5c — the Link icon, when the stage HAS links. */}
              <LinkIcon
                matterRow={matterRow}
                item={{ k: "stage", id: Number(st.id) }}
              />
              {/* §7f — the link pill; 110 §6c — it says the ACTION, not the
                  name: "Show Event", with the event's NAME in the tooltip
                  (where it already lived). Count >1 keeps the existing count
                  text ("{count} events") — that pill was never a name, and
                  the plural phrase already carries what is behind the click. */}
              {linked.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToEv(linked[0].id);
                  }}
                  title={linked
                    .map((e) => String(e.title || ""))
                    .filter(Boolean)
                    .join(" · ")}
                  style={{
                    fontSize: FS.s9_5,
                    color: "var(--tm)",
                    background: "rgba(255,255,255,.07)",
                    padding: "1px 5px",
                    borderRadius: RAD.r8,
                    flexShrink: 0,
                    border: "1px solid var(--br)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    maxWidth: 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {linked.length === 1
                    ? t("stage.show_event", { fallback: "Show Event" })
                    : t("stage.pill.events", {
                        count: linked.length,
                        fallback: `${linked.length} events`,
                      })}
                </button>
              )}
              {!stgLocked && (
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: 3,
                  }}
                  onClick={(e) =>
                    e.stopPropagation()
                  }
                >
                  <button
                    className="btn btn-ghost"
                    style={{
                      padding: "1px 4px",
                      fontSize: FS.s9,
                    }}
                    onClick={() =>
                      onEditStage(st)
                    }
                  >
                    <Pencil size={I.s} />
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{
                      padding: "1px 4px",
                      fontSize: FS.s9,
                    }}
                    onClick={() =>
                      onDeleteStage(st)
                    }
                  >
                    <Trash2 size={I.s} />
                  </button>
                </div>
              )}
            </div>
            {/* §7b — the notes drawer: §1d's discreet pattern, always
                editable (the lock gates structure, never the note). */}
            {/* 111 §4a+§4b — no NOTES heading here (the row names itself; the
                one hideTitle mount), and BOTH drawers take the task/event
                drawers' 40px left indent (`.evSubDrawer`'s `padding-left: 40px`
                — a literal, no token exists to reuse) instead of rendering
                flush with the stage title.
                114 §1 — THE TASK HALF OF THAT CITATION IS WITHDRAWN, and the
                40 stays. The CV/CCV task drawer stopped being a pixel indent:
                it now begins at its row's ASSIGNEE COLUMN, because that is the
                column its subtask badges have to meet. A stage row has no
                badge column to meet — the indent here was only ever "stand off
                the title", which 40 still does. */}
            {notesOpen.has(st.id) && (
              <div style={{ marginTop: 3, marginBottom: 3, paddingLeft: 40 }}>
                <NotesDrawer
                  value={st.notes}
                  canWrite
                  hideTitle
                  onSave={(next) => onSaveStageNotes(st.id, next)}
                />
              </div>
            )}
            {docsShown && (
              <div style={{ marginTop: 4, paddingLeft: 40 }}>
                {stDocs.length === 0 && (
                  <p
                    style={{
                      fontSize: FS.s10,
                      color: "var(--tm)",
                      margin: "2px 0 4px",
                    }}
                  >
                    {t("case.stage.no_docs", {
                      fallback:
                        "No documents in this stage.",
                    })}
                  </p>
                )}
                {/* 151 §3 — THE row (109 §7b's simple [icon · name] grew into
                    the one classification surface once §4 deletes the panes):
                    file-type badge · type of document · title · status ·
                    date of version, in the ruled order. (ix) the whole row
                    opens the link — no separate anchor; (x) unlocked adds the
                    pencil (the one modal, edit variant) and the bin. */}
                {stDocs.map((d: StageDoc) => {
                  const ftI =
                    (FT_INFO as Record<string, { l: string; c: string }>)[
                      d.fileType || detFT(d.name)
                    ] || (FT_INFO as Record<string, { l: string; c: string }>).other;
                  const stI = (docStatusesList || []).find(
                    (x) => x.v === (d.status || ""),
                  );
                  return (
                    <div
                      key={d.id}
                      onClick={() => openDocHref(String(d.link || ""))}
                      title={t("case.doc.open", {
                        fallback: "Open document",
                      })}
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        padding: "3px 0",
                        cursor: "pointer",
                      }}
                    >
                      <DocTypeBadge ftI={ftI} />
                      <span
                        className={`tag ${dcatTag(d.type || "other")}`}
                        style={{ flexShrink: 0 }}
                      >
                        {dcatLabel(d.type || "other")}
                      </span>
                      <span
                        title={String(d.name || d.link || "")}
                        style={{
                          flex: 1,
                          minWidth: 40,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: FS.s11,
                        }}
                      >
                        {String(d.name || d.link || "")}
                      </span>
                      {stI && stI.v !== "" && (
                        <span
                          style={{
                            fontSize: FS.s9,
                            fontWeight: 600,
                            color: stI.c || "var(--tm)",
                            flexShrink: 0,
                          }}
                        >
                          {stI.l}
                        </span>
                      )}
                      {d.date ? (
                        <span
                          className="mono"
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            flexShrink: 0,
                          }}
                        >
                          {fd(d.date)}
                        </span>
                      ) : null}
                      {!stgLocked && (
                        <span
                          style={{ display: "flex", gap: 3, flexShrink: 0 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "1px 4px", fontSize: FS.s9 }}
                            onClick={() => setEditDoc(d)}
                          >
                            <Pencil size={I.s} />
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: "1px 4px", fontSize: FS.s9 }}
                            onClick={() => onDeleteStageDoc(d)}
                          >
                            <Trash2 size={I.s} />
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
                {!stgLocked && (
                  <AddButton
                    label={t("common.add", { fallback: "Add" })}
                    onClick={() => setAddFor(st.id)}
                    style={{
                      padding: "1px 5px",
                      fontSize: FS.s9,
                      marginTop: stDocs.length ? 4 : 0,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
      {/* 151 §2 — §7b's add dialog is now the ONE DocumentEntryModal, and
          step 3 gives it the RICH face: (iv)'s Type · Status of document ·
          Link to Stage · Note, stage REQUIRED and initialised to the drawer
          that opened it. Writes still go through the host (`onCreateStageDoc`),
          one add per opening — but the stageId written is the DRAFT's, so a
          document added from one drawer can be filed into another stage
          without a second dialog. The 109 §7b create-at-once OneDrive arm is
          retired here (the modal's header says why): a pick FILLS the draft
          and the user completes (iv)'s fields before Add. */}
      {(addFor != null || editDoc != null) && !stgLocked && (
        <DocumentEntryModal
          t={t}
          /* 151 §3 (x) — one mount, two modes: the pencil prefills the whole
             row (source derived off the stored fileType, so the date label
             reads right); add opens on the drawer's own stage. */
          editing={editDoc != null}
          initial={
            editDoc
              ? {
                  name: String(editDoc.name || ""),
                  link: String(editDoc.link || ""),
                  fileType: editDoc.fileType || undefined,
                  date: editDoc.date || "",
                  source:
                    editDoc.fileType === "weblink" ? "weblink" : "casefile",
                  type: editDoc.type || undefined,
                  status: editDoc.status || undefined,
                  stageId:
                    editDoc.stageId != null ? Number(editDoc.stageId) : null,
                  note: editDoc.note || undefined,
                }
              : { name: "", link: "", stageId: addFor }
          }
          onClose={() => {
            setAddFor(null);
            setEditDoc(null);
          }}
          onSave={(d) => {
            if (editDoc) onUpdateStageDoc(editDoc.id, d);
            else onCreateStageDoc(Number(d.stageId), d);
            setAddFor(null);
            setEditDoc(null);
          }}
          /* the edit variant's ghost Open (109 §1b) — the plain doc fork;
             stage docs are never message: URIs */
          onOpen={(link) => openDocHref(link)}
          oneDrive={oneDrive}
          classify={{ docTypesList, docStatusesList }}
          stageLink={{
            stages: allStages,
            entries: _caseStageEntries,
            required: true,
          }}
          /* (xi)(c) — the stage variant's THIRD add path: Browse = the OS
             file dialog, then the casefile folder it is saved into. */
          upload={{ pickFolder: true }}
        />
      )}
    </div>
  );
}
